import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormControlLabel,
  Switch,
  Paper,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  InfoOutlined,
  SmsOutlined,
} from "@mui/icons-material";
import { useEffect, useState, useRef } from "react";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  UserAccountType,
  UserFromServer,
} from "../../../__generated__/types";
import {
  useUserContext,
  useUserContextDispatch,
} from "../../../hooks/userContext";
import { Pages } from "../../../utils";
import { useNavigate, Link } from "react-router";
import { useNotification } from "../../../contexts/NotificationContext";
import {
  buildEventIdempotencyKey,
  getFirstTouchAttributionInput,
  readAttributionCookie,
} from "../../../utils/attribution";
import "./styles.scss";

type VerificationMethod = 'email' | 'sms' | 'both';

const isMongoObjectId = (value: string) => /^[a-fA-F0-9]{24}$/.test(value);

export const EmailPasswordFieldset = ({
  isValid,
  step,
  setNextStep,
  setStep,
  setEmail,
  setPassword,
  setConfirmPassword,
  email,
  password,
  confirmPassword,
  setUserAccountId,
  userType,
  phoneNumber,
  setPhoneNumber,
  verificationMethod,
}: {
  isValid: boolean;
  step: number;
  setNextStep: () => void;
  setStep: (value: number) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  email?: string;
  password?: string;
  confirmPassword?: string;
  setUserAccountId: (value: null | string) => void;
  userType?: UserAccountType | null;
  phoneNumber?: string;
  setPhoneNumber?: (value: string) => void;
  verificationMethod?: VerificationMethod;
}) => {
  const { loading, user } = useUserContext();
  const { createNewDesignerAccount, createNewHomeownerAccount } = useUserContextDispatch();
  const navigate = useNavigate();
  const { showSignupError, showSuccess, showError } = useNotification();

  const [error, setError] = useState<string | null>(null);
  const [userExistsError, setUserExistsError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const hasAdvancedStep = useRef(false);
  
  // SMS verification toggle
  const [useSmsVerification, setUseSmsVerification] = useState(false);
  const [internalPhoneNumber, setInternalPhoneNumber] = useState<string>('');

  useEffect(() => {
    // Don't auto-advance for new registrations - they should complete all steps
    // This prevents bypassing email/password creation for payment-verified users
  }, [user, setUserAccountId, setNextStep]);

  useEffect(() => {
    // Only redirect if userType is explicitly invalid (not Designer or Homeowner)
    // Allow undefined userType (it will be set by user selection)
    if (userType && userType !== UserAccountType.Designer && userType !== UserAccountType.Homeowner) {
      navigate(Pages.COMING_SOON);
    }
  }, [userType, navigate]);

  const handleSubmit = async () => {
    if (useSmsVerification) {
      const normalizedPhone = (internalPhoneNumber || "").trim();
      if (!normalizedPhone) {
        setError("Please enter a phone number for SMS verification.");
        showError("Please enter a phone number for SMS verification.");
        return;
      }
    }

    if (!email || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      showError("Please fill in all required fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      showError("Passwords do not match. Please check and try again.");
      return;
    }

    setIsCreatingAccount(true);
    setError(null);
    setUserExistsError(null);

    try {
      const attributionCookie = readAttributionCookie();
      const firstTouchAttribution = getFirstTouchAttributionInput(attributionCookie);
      const accountCreatedIdempotencyKey = buildEventIdempotencyKey(
        "account_created",
        {
          anonymousId: firstTouchAttribution.anonymousId || email,
          variant: firstTouchAttribution.variant,
          scope: email?.toLowerCase(),
        }
      );

      const accountData = {
        email,
        password,
        accountType: userType ?? UserAccountType.Designer,
        // For payment-verified users, use placeholder names that will be updated in Step 4
        firstName: 'Pending',
        lastName: 'Update',
        // Include phone number if SMS verification is enabled
        ...(useSmsVerification && internalPhoneNumber ? { phoneNumber: internalPhoneNumber } : {}),
        idempotencyKey: accountCreatedIdempotencyKey,
        attribution: firstTouchAttribution,
      };


      let result;
      if (userType === UserAccountType.Homeowner) {
        result = await createNewHomeownerAccount(accountData);
      } else {
        result = await createNewDesignerAccount(accountData);
        console.log(result);
      }

      if (result?.data) {
        const returnedAccountId = String(result.data ?? '').trim();
        const isInvalidAccountId =
          !returnedAccountId ||
          returnedAccountId === '[object Object]' ||
          returnedAccountId.startsWith('temp_') ||
          !isMongoObjectId(returnedAccountId);

        if (isInvalidAccountId) {
          const accountError = 'Account setup is still processing. Please wait a moment and try again.';
          showError(accountError);
          setError(accountError);
          console.error('Invalid account ID returned from account creation', {
            returnedAccountId,
            email,
            userType,
          });
          return;
        }

        showSuccess(
          `We’ve sent a verification link to your email. Please check your inbox (and spam folder).`
        );
        // Set the user account ID and advance to next step
        setUserAccountId(returnedAccountId);
        setNextStep();
        hasAdvancedStep.current = true;
        setIsCreatingAccount(false);
      } else if (result?.error) {
        const errorMessage = typeof result.error === 'string' ? result.error : (result.error as any)?.message || 'Unknown error';
        
        // Handle specific error cases
        if (errorMessage.toLowerCase().includes('user already exists') || 
            errorMessage.toLowerCase().includes('already registered')) {
          showError('An account with this email already exists. Please log in or use a different email.');
          console.error('Signup blocked: duplicate email', {
            email,
            userType,
            smsRequested: useSmsVerification,
            phoneProvided: !!internalPhoneNumber,
          });
          setIsCreatingAccount(false);
          return;
        } else if (errorMessage.toLowerCase().includes('email')) {
          showError('Invalid email address. Please check your email and try again.');
        } else {
          showError(errorMessage || 'Failed to create account. Please try again.');
        }
        
        console.error('Signup error during account creation', {
          message: errorMessage,
          email,
          userType,
          smsRequested: useSmsVerification,
          phoneProvided: !!internalPhoneNumber,
        });

        setError(errorMessage);
        setIsCreatingAccount(false);
      }
    } catch (error: any) {
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setIsCreatingAccount(false);
      showError(errorMessage);
      console.error('Unexpected signup error', {
        error: error?.message || error,
        email,
        userType,
        smsRequested: useSmsVerification,
        phoneProvided: !!internalPhoneNumber,
      });
      setError(errorMessage);
    } finally {
      setIsCreatingAccount(false);
    }
  };

  useEffect(() => {
    if (
      email &&
      password &&
      confirmPassword &&
      password === confirmPassword
    ) {
      setError(null);
    }
  }, [email, password, confirmPassword]);

  const passwordRules = [
    {
      label: "Between 8 and 30 characters",
      test: (pwd: string) => pwd.length >= 8 && pwd.length <= 30,
    },
    {
      label: "At least 1 number",
      test: (pwd: string) => /\d/.test(pwd),
    },
    {
      label: "At least 1 letter",
      test: (pwd: string) => /[a-zA-Z]/.test(pwd),
    },
    {
      label: "At least 1 special character",
      test: (pwd: string) => /[^a-zA-Z0-9]/.test(pwd),
    },
  ];

  return (
    <Box className="email-password-wrapper">
      <Container>
        <Typography variant="h4" gutterBottom>
          Set Up Your Account
        </Typography>
        <Typography variant="body1" gutterBottom>
          Let’s create your password.
        </Typography>

        {loading && <CircularProgress sx={{ my: 4, color: '#00A06D' }} />}

        {!loading && (
          <>
            <TextField
              fullWidth
              label="Email Address or Username"
              margin="normal"
              required
              type="email"
              value={email || ''}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear user exists error when user starts typing a different email
                if (userExistsError) {
                  setUserExistsError(null);
                }
              }}
            />

            <TextField
              fullWidth
              label="Password"
              margin="normal"
              required
              type={showPassword ? "text" : "password"}
              value={password || ''}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              margin="normal"
              required
              type="password"
              value={confirmPassword || ''}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            {/* SMS Verification Toggle */}
            <Paper
              elevation={useSmsVerification ? 2 : 0}
              sx={{
                mt: 3,
                p: 2,
                bgcolor: useSmsVerification ? '#F0F9FF' : '#F9FAFB',
                border: useSmsVerification ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmsOutlined sx={{ color: useSmsVerification ? '#3B82F6' : '#6B7280' }} />
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      SMS Verification
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Get your code via text message
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useSmsVerification}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setUseSmsVerification(enabled);
                        if (!enabled) {
                          setInternalPhoneNumber('');
                          if (setPhoneNumber) setPhoneNumber('');
                        }
                      }}
                      color="primary"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>

              {/* Phone Number Input - Show when SMS is toggled on */}
              {useSmsVerification && (
                <Box sx={{ mt: 2, animation: 'fadeIn 0.3s ease' }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                    Phone Number (Required for SMS)
                  </Typography>
                  <Box
                    sx={{
                      '& .PhoneInput': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '& .PhoneInputInput': {
                        flex: 1,
                        padding: '16.5px 14px',
                        fontSize: '1rem',
                        border: '2px solid #3B82F6',
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                          borderColor: '#2563EB',
                        },
                        '&:focus': {
                          outline: 'none',
                          borderColor: '#1D4ED8',
                          boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                        },
                      },
                      '& .PhoneInputCountry': {
                        marginRight: '8px',
                      },
                    }}
                  >
                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={internalPhoneNumber}
                      onChange={(value) => {
                        setInternalPhoneNumber(value || '');
                        if (setPhoneNumber) {
                          setPhoneNumber(value || '');
                        }
                      }}
                      placeholder="(555) 123-4567"
                    />
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlined sx={{ fontSize: 14, color: '#6B7280' }} />
                    <Typography variant="caption" color="text.secondary">
                      Standard messaging rates may apply from your carrier
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

            <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
              Your password must have:
            </Typography>

            <List dense>
              {passwordRules.map((rule) => {
                const passed = rule.test(password || "");
                return (
                  <ListItem key={rule.label}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {passed ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Tooltip title="Required format">
                          <Cancel color="disabled" fontSize="small" />
                        </Tooltip>
                      )}
                    </ListItemIcon>
                    <ListItemText primary={rule.label} />
                  </ListItem>
                );
              })}
            </List>

            {userExistsError && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
                <Typography color="error" sx={{ display: 'inline' }}>
                  {userExistsError}
                </Typography>
                {' '}
                <Link 
                  to={Pages.LOGIN} 
                  style={{ 
                    color: '#1976d2', 
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'underline'}
                  onMouseLeave={(e) => (e.target as HTMLAnchorElement).style.textDecoration = 'none'}
                >
                  Sign in here
                </Link>
              </Box>
            )}

            {error && !userExistsError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={!isValid || isCreatingAccount}
              onClick={handleSubmit}
              startIcon={isCreatingAccount ? <CircularProgress size={20} sx={{ color: '#00A06D' }} /> : undefined}
              sx={{ 
                mt: 4, 
                py: 1.5, 
                fontWeight: "bold", 
                fontSize: "1rem", 
                borderRadius: "8px",
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
              }}
            >
              {isCreatingAccount ? 'Creating Account...' : 'Continue'}
            </Button>
          </>
        )}
      </Container>
    </Box>
  );
};
